import {
  ChangeEvent,
  FC,
  FormEventHandler,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  useLazyAddressCompleteQuery,
  useLazyAddressDetailsQuery,
} from '~/CreateFabricInvitation/api/endpoints/addressComplete';
import {ConfirmRemoveSectionModal} from '~/CreateFabricInvitation/routes/EventDetails/components/ConfirmRemoveSectionModal';
import {UseMapLocationParams, useMapLocation} from '~/common/hooks/useMapLocation';
import {Address, AddressResponse, Place} from '~/common/types/location';
import {CustomInputWithIcon} from '~/OneStepCreateInvitation/routes/OneStepCreatePage/components/DetailsFields/components/CustomInputWithIcon/CustomInputWithIcon';
import {Button} from '~library/atoms/Button';
import {Input} from '~library/atoms/Input';
import {AddressSelect} from '~library/molecules/AddressSelect';
import {ResponsivePanel} from '~library/organisms/ResponsivePanel';
import {XIcon} from '~library/svg/v2/XIcon';

import './AddressOverlay.sass';

interface OverlayContentProps extends UseMapLocationParams {
  isOverlayOpen: boolean;
}

const OverlayContent: FC<OverlayContentProps> = ({isOverlayOpen, ...useMapLocationParams}) => {
  const locationNameInputRef = useRef<HTMLInputElement>(null);
  const unitNumInputRef = useRef<HTMLInputElement>(null);
  const addressInputRef = useRef<HTMLInputElement>(null);
  const [lazyAddressCompleteQuery] = useLazyAddressCompleteQuery();
  const [lazyAddressDetailsQuery] = useLazyAddressDetailsQuery();
  const {
    location,
    address,
    placeId,
    meetingUrl,
    showMap,
    hasAddress,
    hasHttpAddress,
    hasPhysicalAddress,
    hasPlaceId,
    hasLocation,
    formatAddress,
    updateAllLocation,
    updateShowMap,
    updateLocation,
    updateAddress,
  } = useMapLocationParams;

  const [selectorValue, setSelectorValue] = useState('');
  const [initValue, setInitValue] = useState('');
  const [initMenu, setInitMenu] = useState(false);
  const [editing, setEditing] = useState(!hasAddress && !hasHttpAddress && !hasLocation);
  const [showUnitNum, setShowUnitNum] = useState(false);

  const toggleEditing = useCallback(() => {
    setEditing(!editing);
    if (!editing) {
      let buff = '';
      if (meetingUrl) buff = meetingUrl;
      if (location && !address.street) buff = location;
      if (address.street) buff += `${address.street}, `;
      if (address.city) buff += `${address.city}, `;
      if (address.state) buff += `${address.state} `;
      if (address.zip) buff += `${address.zip} `;
      if (address.country) buff += `${address.country} `;
      buff = buff.trimEnd();
      setSelectorValue(buff);
      setInitValue(buff);
      setInitMenu(true);
    }
  }, [editing, meetingUrl, location, address]);

  const toggleShowUnitNum = useCallback(() => {
    setShowUnitNum(true);
  }, []);

  const onAddressSelectChange = useCallback(
    (data: AddressResponse) => {
      const newAddr: Address = {street: '', city: '', state: '', zip: '', country: '', unitNum: ''};
      const newId = data.address?.place_id || null;
      const newUrl = data.meetingUrl || null;
      let newLoc = data.meetingUrl ? '' : location;

      if (data.clear) newLoc = '';

      if (data.location) {
        newLoc = data.location;
        setShowUnitNum(false);
      } else if (!newUrl && !data.clear) {
        if (data.address.name && data.address.name !== data.address.street)
          newLoc = data.address.name;
        else newLoc = '';
        newAddr.unitNum = '';
        newAddr.street = data.address.street;
        newAddr.zip = data.address.zip;
        newAddr.city = data.address.city;
        newAddr.state = data.address.state;
        newAddr.country = data.address.country;
      } else {
        setShowUnitNum(false);
      }

      updateAllLocation({
        address: newAddr,
        meetingUrl: newUrl,
        placeId: newId,
        location: newLoc,
        showMap,
      });
      setEditing(!newAddr.street && !newAddr.city && !newUrl && !newLoc);
    },
    [location, showMap, updateAllLocation]
  );

  const showEditing = useMemo(
    () => editing || (!hasPhysicalAddress && !hasLocation && !hasHttpAddress),
    [editing, hasPhysicalAddress, hasLocation, hasHttpAddress]
  );

  useEffect(() => {
    if (isOverlayOpen) return;
    // Reset editing state when the overlay is closed
    setEditing(!hasAddress && !hasHttpAddress && !hasLocation);
  }, [isOverlayOpen, hasAddress, hasHttpAddress, hasLocation]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: deps
  useEffect(() => {
    if (isOverlayOpen && !address.unitNum && showUnitNum) {
      unitNumInputRef.current?.focus();
    }
  }, [showUnitNum]);

  useEffect(() => {
    if (!isOverlayOpen) return;
    if (showEditing) {
      addressInputRef.current?.focus();
      return;
    }
    locationNameInputRef.current?.focus();
  }, [isOverlayOpen, showEditing]);

  const toggleMap = useCallback(() => {
    updateShowMap(!showMap);
  }, [showMap, updateShowMap]);

  const getAddressComplete = useCallback(
    async (query: string): Promise<Place[]> => {
      const data = await lazyAddressCompleteQuery(query).unwrap();
      return data.results;
    },
    [lazyAddressCompleteQuery]
  );

  const getAddressDetails = useCallback(
    async (place: string): Promise<Address> => lazyAddressDetailsQuery(place).unwrap(),
    [lazyAddressDetailsQuery]
  );

  const handleChangeLocation = useCallback(
    ({target: {value: newLocation}}: ChangeEvent<HTMLInputElement>) => updateLocation(newLocation),
    [updateLocation]
  );

  const handleChangeUnitNum = useCallback(
    ({target: {value: unitNum}}: ChangeEvent<HTMLInputElement>) =>
      updateAddress({...address, unitNum}),
    [address, updateAddress]
  );

  const handleRemoveUnitNum = useCallback(
    () => updateAddress({...address, unitNum: ''}),
    [updateAddress, address]
  );

  return (
    <div className="address-overlay__content">
      {hasPhysicalAddress && (
        <>
          <div className="label2 small input-label">Location</div>
          <Input
            className="address-overlay__location-value"
            id="location"
            name="location"
            data-qa-id="event-location"
            type="text"
            value={location}
            onChange={handleChangeLocation}
            placeholder="Optional name (e.g.: My Place)"
            ref={locationNameInputRef}
          />
        </>
      )}
      {showEditing ? (
        <AddressSelect
          getAddressComplete={getAddressComplete}
          getAddressDetails={getAddressDetails}
          data-qa-id="address-autocomplete"
          onChange={onAddressSelectChange}
          onStopEditing={toggleEditing}
          initValue={initValue}
          initMenu={initMenu}
          value={selectorValue}
          ref={addressInputRef}
        />
      ) : (
        <div>
          <CustomInputWithIcon
            name="address"
            dataQaId="event-address"
            type="text"
            centerIcon
            value={formatAddress(false)}
            onClick={toggleEditing}
            fullWidth
          />
        </div>
      )}

      {(showUnitNum || address.unitNum) && (
        <div className="address-overlay__address-unit-num">
          <Input
            className="address-overlay__address-unit-num"
            label="Apt / Unit"
            id="unitNum"
            name="unitNum"
            data-qa-id="event-address-unit-num"
            type="text"
            ref={unitNumInputRef}
            value={address.unitNum}
            onChange={handleChangeUnitNum}
            placeholder="ex. Suite 38483"
            endAdornment={
              address.unitNum ? (
                <Button
                  variant="unstyled-button"
                  className="address-select__clear"
                  onClick={handleRemoveUnitNum}
                  data-qa-id="event-address-unit-num__remove"
                >
                  <XIcon ratio={0.5} />
                </Button>
              ) : null
            }
          />
        </div>
      )}
      <div className="address-overlay__lastline">
        {placeId && (
          <Input
            data-qa-id="address-overlay-show-map"
            type="toggle"
            label="Show&nbsp;Map"
            checked={showMap}
            onChange={toggleMap}
          />
        )}
        {!showUnitNum && hasPlaceId && !address.unitNum && (
          <div className="add-wrapper">
            <Button
              id="addUnitNum"
              className="address-overlay__add-unit-num"
              data-qa-id="address-overlay__add-unit-num"
              variant="link"
              size="small"
              type="button"
              onClick={toggleShowUnitNum}
            >
              + Add Apt / Unit
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

interface AddressOverlayProps {
  closeOverlay: () => void;
  isOpen: boolean;
  title?: string;
}

export const AddressOverlayContainer: FC<
  AddressOverlayProps & {location: UseMapLocationParams}
> = ({closeOverlay, isOpen, title = 'Location', location}) => {
  const {hasLocation, propagateLocationDetails, resetLocationDetails, removeLocation} = location;
  const [initialHasLocation, setInitialHasLocation] = useState(hasLocation);
  const [isConfirmRemoveModalOpen, setIsConfirmRemoveModalOpen] = useState(false);

  useEffect(() => {
    setInitialHasLocation((prev) => {
      if (isOpen) return prev;
      return hasLocation;
    });
  }, [isOpen, hasLocation]);

  const onSaveHandler: FormEventHandler = useCallback(
    (e) => {
      e.preventDefault();
      e.stopPropagation();
      propagateLocationDetails();
      closeOverlay();
    },
    [propagateLocationDetails, closeOverlay]
  );

  const onCloseHandler = useCallback(() => {
    resetLocationDetails();
    closeOverlay();
  }, [resetLocationDetails, closeOverlay]);

  const onRemoveLocationHandler = useCallback(() => {
    setIsConfirmRemoveModalOpen(true);
  }, []);

  const onCloseConfirmRemoveModalHandler = useCallback(() => {
    setIsConfirmRemoveModalOpen(false);
  }, []);

  const onConfirmRemoveLocationHandler = useCallback(() => {
    removeLocation();
    closeOverlay();
    onCloseConfirmRemoveModalHandler();
  }, [removeLocation, closeOverlay, onCloseConfirmRemoveModalHandler]);

  return (
    <ResponsivePanel
      isOpen={isOpen}
      onClose={onCloseHandler}
      title={title}
      modalId="address"
      showCover
      renderMobileViewAs="bottom-sheet"
      disableOverlayClick
    >
      <form onSubmit={onSaveHandler} className="address-overlay__content-wrapper">
        <OverlayContent isOverlayOpen={isOpen} {...location} />
        <Button
          data-qa-id="event-location-save"
          variant="primary"
          size="large"
          formSubmit
          type="submit"
        >
          Save
        </Button>
        {initialHasLocation && (
          <Button
            data-qa-id="event-location-remove"
            variant="transparent"
            onClick={onRemoveLocationHandler}
            size="large"
            className="address-overlay__remove-button"
          >
            Clear location
          </Button>
        )}
        {initialHasLocation && isConfirmRemoveModalOpen && (
          <ConfirmRemoveSectionModal
            message="Are you sure you want to clear this location?"
            primaryCtaText="Yes, clear it"
            isOpen
            onClose={onCloseConfirmRemoveModalHandler}
            onConfirm={onConfirmRemoveLocationHandler}
            qaId="confirm-clear-location"
          />
        )}
      </form>
    </ResponsivePanel>
  );
};

export const AddressOverlay: FC<AddressOverlayProps> = ({
  closeOverlay,
  isOpen,
  title = 'Location',
}) => {
  const location = useMapLocation();
  return (
    <AddressOverlayContainer
      closeOverlay={closeOverlay}
      isOpen={isOpen}
      title={title}
      location={location}
    />
  );
};
